/**
 * Servicio centralizado de API
 * Principios SOLID aplicados:
 * - SRP: Responsabilidad única de gestionar llamadas HTTP
 * - OCP: Extensible para nuevos endpoints sin modificar código existente
 * - DIP: Los componentes dependen de este servicio, no de fetch directamente
 */

import { API_CONFIG } from '../constants/config';
import { fetchAuthSession } from 'aws-amplify/auth';

class ApiService {
  constructor(baseURL = API_CONFIG.ADMIN_API_URL) {
    this.baseURL = baseURL;
    this.timeout = API_CONFIG.TIMEOUT;
  }

  /**
   * Obtiene el ID token JWT de Cognito.
   * Amplify maneja el refresh automáticamente.
   * @private
   */
  async _getAuthToken() {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch {
      return null;
    }
  }

  /**
   * Método genérico para realizar peticiones HTTP
   * @private
   */
  async _request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    // Obtener token de autenticación
    const token = await this._getAuthToken();
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      // Implementar timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
        cache: 'no-store',
      });

      clearTimeout(timeoutId);

      // Verificar si la respuesta es exitosa
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('La petición excedió el tiempo de espera');
      }
      throw error;
    }
  }

  /**
   * GET request
   */
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    
    return this._request(url, {
      method: 'GET',
    });
  }

  /**
   * POST request
   */
  async post(endpoint, data = {}) {
    return this._request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * PUT request
   */
  async put(endpoint, data = {}) {
    return this._request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * DELETE request
   */
  async delete(endpoint) {
    return this._request(endpoint, {
      method: 'DELETE',
    });
  }

  /**
   * PATCH request
   */
  async patch(endpoint, data = {}) {
    return this._request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Health check del sistema — consulta admin-handler Lambda.
   * Endpoint: GET /admin/health (requiere JWT admin)
   * Devuelve null si no responde.
   */
  async getHealth() {
    try {
      return await this.get('/admin/health');
    } catch {
      return null;
    }
  }
}

// Exportar como singleton — apunta a AWS API Gateway
const ApiServiceInstance = new ApiService();
export default ApiServiceInstance;

// Alias mantenido por compatibilidad con imports existentes
export const adminApi = ApiServiceInstance;
